"use client";

import { useActionState, useEffect, useState, useTransition } from "react";
import { useForm } from "react-hook-form";
import { submitContactForm } from "@/app/actions/contactForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { countries } from "@/lib/countries";
import { cn } from "@/lib/utils";
interface FormState {
  success: boolean;
  message: string;
  errors: {
    errors: string[];
    properties?: {
      firstName?: {
        errors: string[];
      };
      lastName?: {
        errors: string[];
      };
      businessEmail?: {
        errors: string[];
      };
      phoneNumber?: {
        errors: string[];
      };
      jobTitle?: {
        errors: string[];
      };
      companyOrInstitution?: {
        errors: string[];
      };
      country?: {
        errors: string[];
      };
      message?: {
        errors: string[];
      };
    };
  } | null;
}
const initialState: FormState = {
  success: false,
  message: "",
  errors: {
    errors: []
  }
};
export default function ContactForm() {
  const [state, formAction] = useActionState(submitContactForm, initialState);
  const [isPending, startTransition] = useTransition();
  const [formKey, setFormKey] = useState(0);
  const form = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      businessEmail: "",
      phoneNumber: "",
      jobTitle: "",
      companyOrInstitution: "",
      country: "",
      message: ""
    }
  });
  useEffect(() => {
    if (state.errors?.properties) {
      const fieldsWithErrors = Object.keys(state.errors.properties) as Array<keyof typeof state.errors.properties>;
      form.reset(prevValues => {
        const newValues = {
          ...prevValues
        };
        fieldsWithErrors.forEach(field => {
          newValues[field] = "";
        });
        return newValues;
      });
      setFormKey(prev => prev + 1);
    }
  }, [state.errors, form]);
  const onSubmit = async (data: typeof form.control._defaultValues) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value);
    });
    startTransition(() => {
      formAction(formData);
    });
  };
  return <Card className="bg-background/80 backdrop-blur-sm border-border/50">
      <CardContent className="p-6">
        {state.success ? <div className="text-center py-12">
            <div className="text-green-500 text-6xl mb-4">✓</div>
            <h2 className="text-xl font-bold text-foreground mb-2">
              ¡Mensaje enviado!
            </h2>
            <p className="text-muted-foreground">
              {state.message || "Gracias por contactarnos. Pronto le responderemos."}
            </p>
          </div> : <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" key={formKey}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">
                  Nombre <span className="text-primary">*</span>
                </Label>
                <Input id="firstName" {...form.register("firstName")} placeholder="Juan" className={cn(form.formState.errors.firstName && "border-destructive")} />
                {form.formState.errors.firstName && <p className="text-destructive text-[10px]">
                    {form.formState.errors.firstName.message}
                  </p>}
                {state.errors?.properties?.firstName?.errors && <p className="text-destructive text-[10px]">
                    {state.errors.properties.firstName.errors[0]}
                  </p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">
                  Apellidos <span className="text-primary">*</span>
                </Label>
                <Input id="lastName" {...form.register("lastName")} placeholder="Pérez García" className={cn(form.formState.errors.lastName && "border-destructive")} />
                {form.formState.errors.lastName && <p className="text-destructive text-[10px]">
                    {form.formState.errors.lastName.message}
                  </p>}
                {state.errors?.properties?.lastName?.errors && <p className="text-destructive text-[10px]">
                    {state.errors.properties.lastName.errors[0]}
                  </p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="businessEmail">
                  Email corporativo <span className="text-primary">*</span>
                </Label>
                <Input id="businessEmail" {...form.register("businessEmail")} type="email" placeholder="juan@empresa.com" className={cn(form.formState.errors.businessEmail && "border-destructive")} />
                {form.formState.errors.businessEmail && <p className="text-destructive text-[10px]">
                    {form.formState.errors.businessEmail.message}
                  </p>}
                {state.errors?.properties?.businessEmail?.errors && <p className="text-destructive text-[10px]">
                    {state.errors.properties.businessEmail.errors[0]}
                  </p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phoneNumber">
                  Teléfono <span className="text-primary">*</span>
                </Label>
                <Input id="phoneNumber" {...form.register("phoneNumber")} placeholder="+34 600 123 456" className={cn(form.formState.errors.phoneNumber && "border-destructive")} />
                {form.formState.errors.phoneNumber && <p className="text-destructive text-[10px]">
                    {form.formState.errors.phoneNumber.message}
                  </p>}
                {state.errors?.properties?.phoneNumber?.errors && <p className="text-destructive text-[10px]">
                    {state.errors.properties.phoneNumber.errors[0]}
                  </p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="jobTitle">
                  Cargo / Puesto <span className="text-primary">*</span>
                </Label>
                <Input id="jobTitle" {...form.register("jobTitle")} placeholder="Director de Tecnología" className={cn(form.formState.errors.jobTitle && "border-destructive")} />
                {form.formState.errors.jobTitle && <p className="text-destructive text-[10px]">
                    {form.formState.errors.jobTitle.message}
                  </p>}
                {state.errors?.properties?.jobTitle?.errors && <p className="text-destructive text-[10px]">
                    {state.errors.properties.jobTitle.errors[0]}
                  </p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyOrInstitution">
                  Empresa o Institución <span className="text-primary">*</span>
                </Label>
                <Input id="companyOrInstitution" {...form.register("companyOrInstitution")} placeholder="Empresa S.L." className={cn(form.formState.errors.companyOrInstitution && "border-destructive")} />
                {form.formState.errors.companyOrInstitution && <p className="text-destructive text-[10px]">
                    {form.formState.errors.companyOrInstitution.message}
                  </p>}
                {state.errors?.properties?.companyOrInstitution?.errors && <p className="text-destructive text-[10px]">
                    {state.errors.properties.companyOrInstitution.errors[0]}
                  </p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">
                País <span className="text-primary">*</span>
              </Label>
              <Select value={form.watch("country")} onValueChange={value_0 => form.setValue("country", value_0)}>
                <SelectTrigger id="country" className={cn((form.formState.errors.country || state.errors?.properties?.country?.errors) && "border-destructive")}>
                  <SelectValue placeholder="Seleccione un país" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map(country => <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              {form.formState.errors.country && <p className="text-destructive text-[10px]">
                  {form.formState.errors.country.message}
                </p>}
              {state.errors?.properties?.country?.errors && <p className="text-destructive text-[10px]">
                  {state.errors.properties.country.errors[0]}
                </p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">
                Mensaje <span className="text-primary">*</span>
              </Label>
              <Textarea id="message" {...form.register("message")} placeholder="Describa brevemente su caso..." rows={5} className={cn(form.formState.errors.message && "border-destructive")} />
              {form.formState.errors.message && <p className="text-destructive text-[10px]">
                  {form.formState.errors.message.message}
                </p>}
              {state.errors?.properties?.message?.errors && <p className="text-destructive text-[10px]">
                  {state.errors.properties.message.errors[0]}
                </p>}
            </div>

            {state.errors?.errors && state.errors.errors.length > 0 && <div className="bg-destructive/10 border border-destructive/20 text-destructive text-xs p-3 rounded-none">
                {state.errors.errors[0]}
              </div>}

            {state.message && !state.success && <div className="bg-destructive/10 border border-destructive/20 text-destructive text-xs p-3 rounded-none">
                {state.message}
              </div>}

            <Button type="submit" disabled={isPending} className="w-full hover:translate-y-0.5 transition duration-200" size="lg">
              {isPending ? "Enviando..." : "Enviar"}
            </Button>
          </form>}
      </CardContent>
    </Card>;
}